import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createWhatsappTemplateApi,
  deleteWhatsappTemplateApi,
  getWhatsappTemplateApi,
  getWhatsappTemplatesApi,
  sendWhatsappTemplateApi,
  updateWhatsappTemplateApi,
} from "../../api/ecommerce/WhatsappTemplateApi";

const initialState = {
  template: null,
  templates: [],
  listing: false,
  createdTemplate: null,
  updatedTemplate: null,
  deletedTemplate: null,
  sentTemplate: null,
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  sending: false,
  error: null,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

const unwrapResponse = (payload) => payload?.response ?? payload;

export const getWhatsappTemplates = createAsyncThunk(
  "whatsappTemplate/getWhatsappTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWhatsappTemplatesApi();
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getWhatsappTemplate = createAsyncThunk(
  "whatsappTemplate/getWhatsappTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await getWhatsappTemplateApi(templateId);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// Dispatch with { templateId, body } for updates.
export const updateWhatsappTemplate = createAsyncThunk(
  "whatsappTemplate/updateWhatsappTemplate",
  async ({ templateId, body }, { rejectWithValue }) => {
    try {
      const response = await updateWhatsappTemplateApi(templateId, body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteWhatsappTemplate = createAsyncThunk(
  "whatsappTemplate/deleteWhatsappTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await deleteWhatsappTemplateApi(templateId);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// body: { name, language, category, components }
export const createWhatsappTemplate = createAsyncThunk(
  "whatsappTemplate/createWhatsappTemplate",
  async (body, { rejectWithValue }) => {
    try {
      const response = await createWhatsappTemplateApi(body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// body: { to, name, language, components?: [{ type, parameters }] }
export const sendWhatsappTemplate = createAsyncThunk(
  "whatsappTemplate/sendWhatsappTemplate",
  async (body, { rejectWithValue }) => {
    try {
      const response = await sendWhatsappTemplateApi(body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const whatsappTemplateSlice = createSlice({
  name: "whatsappTemplate",
  initialState,
  reducers: {
    clearWhatsappTemplateError: (state) => {
      state.error = null;
    },
    clearWhatsappTemplateState: () => initialState,
  },
  extraReducers: (builder) => {
    const operations = [
      [getWhatsappTemplates, "templates", "listing"],
      [getWhatsappTemplate, "template", "loading"],
      [createWhatsappTemplate, "createdTemplate", "creating"],
      [updateWhatsappTemplate, "updatedTemplate", "updating"],
      [deleteWhatsappTemplate, "deletedTemplate", "deleting"],
      [sendWhatsappTemplate, "sentTemplate", "sending"],
    ];

    operations.forEach(([thunk, dataKey, loadingKey]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[loadingKey] = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[loadingKey] = false;
          state[dataKey] = unwrapResponse(action.payload);
        })
        .addCase(thunk.rejected, (state, action) => {
          state[loadingKey] = false;
          state.error =
            action.payload?.errorMessage ||
            action.error?.message ||
            "Something went wrong";
        });
    });
  },
});

export const { clearWhatsappTemplateError, clearWhatsappTemplateState } =
  whatsappTemplateSlice.actions;

export default whatsappTemplateSlice.reducer;
